import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import oauthService from '@/services/oauthService';
import { navigate } from '@/utils/navigation';
import { applicationName } from '@/utils/requestedAccess';
import { safeReturnTo } from '@/utils/returnTo';

const APPLICATION_SIGN_IN = '/oauth/login?';

/** The application sign-in a portal page returns to, if its return address is one. */
export function loginChallengeOf(returnTo: unknown): string {
  const address = safeReturnTo(returnTo);
  if (!address?.startsWith(APPLICATION_SIGN_IN)) return '';
  return (
    new URLSearchParams(address.slice(APPLICATION_SIGN_IN.length)).get('login_challenge') ?? ''
  );
}

/**
 * The application a person signs in or registers for, named on the portal's own pages, and a
 * way to cancel its sign-in. The name stays empty, and the page plain, when the request cannot
 * be read.
 */
export function useApplicationSignIn() {
  const route = useRoute();
  const router = useRouter();
  const challenge = computed(() => loginChallengeOf(route.query.returnTo));
  const application = ref('');
  const cancelling = ref(false);

  watch(
    challenge,
    async (value) => {
      application.value = '';
      if (!value) return;
      try {
        const name = applicationName(await oauthService.fetchLoginRequest(value));
        if (value === challenge.value) application.value = name;
      } catch {
        // The sign-in page works the same without the name.
      }
    },
    { immediate: true }
  );

  async function cancel() {
    if (!challenge.value || cancelling.value) return;
    cancelling.value = true;
    try {
      navigate((await oauthService.respondToLoginRequest(challenge.value, 'reject')).redirect_to);
    } catch {
      // The application sign-in page explains an expired request.
      cancelling.value = false;
      await router.push(
        `${APPLICATION_SIGN_IN}${new URLSearchParams({ login_challenge: challenge.value })}`
      );
    }
  }

  return { application, cancel, cancelling };
}
